import { H2 } from 'foundations-components/transitional/components/H2';
import { H3 } from 'foundations-components/transitional/components/H3';
import { H1 } from 'foundations-components/transitional/components/H1';
import styled from 'foundations-theming/utils';
import PublicPageBreadcrumbs from './PublicPageBreadcrumbs';
import I18n from 'I18n';
import EmbeddedSignup from '../../embeddedSignup/components/EmbeddedSignup';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { forwardRef } from 'react';
import { View } from 'foundations-components/components/View';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { getMarketingThemePreference } from '../../../utils/themeUtils';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const GradientBackground = styled.div.withConfig({
  displayName: "PublicLandingPagePrimaryDetails__GradientBackground",
  componentId: "zzz75q-0"
})(["position:absolute;z-index:-1;top:0;left:calc((100vw - 100%) / 2 * -1);height:", ";", " width:100vw;&::before{content:'';display:block;background:", ";width:100%;height:100%;}"], ({
  isMobile
}) => isMobile ? 'calc(100% + 56px)' : 'calc(100% + 126px)', ({
  variant
}) => variant === 'marketing' && `
    height: 100%;
  `, props => props.variant === 'blue' ? `linear-gradient(45deg, ${props.theme.color['fill-accent-teal-pressed']}, ${props.theme.color['fill-accent-teal-default']})` : props.variant === 'green' ? `linear-gradient(45deg, ${props.theme.color['fill-accent-green-pressed']}, ${props.theme.color['fill-accent-green-default']})` : `${props.theme.color['fill-surface-recessed']}`);
const GradientSvg = () => /*#__PURE__*/_jsx("svg", {
  preserveAspectRatio: "xMinYMin meet",
  style: {
    position: 'absolute',
    bottom: '-1px',
    left: '0',
    width: '100%'
  },
  viewBox: "0 0 500 50",
  children: /*#__PURE__*/_jsx("path", {
    d: "M0,50 L0,4 C95,-23 285,115 500,2 L500,50 L0,50 Z",
    style: {
      stroke: 'none',
      fill: 'rgb(255, 255, 255)'
    }
  })
});
const Description = styled.p.withConfig({
  displayName: "PublicLandingPagePrimaryDetails__Description",
  componentId: "zzz75q-1"
})(["color:", ";a{text-decoration:underline;&:hover{color:", ";}}"], ({
  $use,
  theme
}) => $use === 'on-dark' ? theme.color['text-core-on-fill-default'] : theme.color['text-core-default'], ({
  $use,
  theme
}) => $use === 'on-dark' ? theme.color['text-interactive-on-fill-hover'] : theme.color['text-interactive-default-hover']);
const CONTENT_TYPE_MESSAGE = {
  CERTIFICATION: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "academyPublic.contentLandingPage.primaryDetails.contentType.certification"
  }),
  TRACK: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "academyPublic.contentLandingPage.primaryDetails.contentType.course"
  }),
  LESSON: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "academyPublic.contentLandingPage.primaryDetails.contentType.lesson"
  })
};
const PublicLandingPagePrimaryDetails = /*#__PURE__*/forwardRef(({
  contentType,
  headline,
  name,
  description,
  parentName,
  parentPath,
  id,
  pagePath
}, ref) => {
  const {
    isMobile
  } = useMediaQuery();
  const isMarketingThemeEnabled = getMarketingThemePreference();
  return /*#__PURE__*/_jsxs(View, {
    as: "section",
    marginBlockEnd: isMarketingThemeEnabled ? '800' : '1500',
    minBlockSize: "450px",
    paddingBlockEnd: "800",
    paddingBlockStart: "800",
    position: "relative",
    children: [/*#__PURE__*/_jsx(GradientBackground, {
      isMobile: isMobile,
      variant: isMarketingThemeEnabled ? 'marketing' : contentType === 'LESSON' ? 'green' : 'blue',
      children: !isMarketingThemeEnabled && /*#__PURE__*/_jsx(GradientSvg, {})
    }), /*#__PURE__*/_jsx(PublicPageBreadcrumbs, {
      entries: [{
        name: I18n.text('academyPublic.contentLandingPage.primaryDetails.breadcrumbs.home'),
        to: '/'
      }, {
        name: I18n.text('academyPublic.contentLandingPage.primaryDetails.breadcrumbs.library'),
        to: '/courses'
      }, ...(parentName && parentPath ? [{
        name: parentName,
        to: `/courses/${parentPath}`
      }] : []), ...(name ? [{
        name
      }] : [])],
      use: isMarketingThemeEnabled ? 'on-light' : 'on-dark'
    }), /*#__PURE__*/_jsxs(CardGrid, {
      gap: "1400",
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))',
      children: [/*#__PURE__*/_jsxs("div", {
        children: [headline && /*#__PURE__*/_jsxs(H2, {
          color: isMarketingThemeEnabled ? 'text-core-default' : 'text-core-on-fill-default',
          "data-test-id": "public-course-headline",
          children: [headline, ":"]
        }), name && /*#__PURE__*/_jsx(H1, {
          color: isMarketingThemeEnabled ? 'text-core-default' : 'text-core-on-fill-default',
          "data-test-id": "public-course-name",
          children: name
        }), isMarketingThemeEnabled ? /*#__PURE__*/_jsx(Tag, {
          marginBlockEnd: "400",
          children: CONTENT_TYPE_MESSAGE[contentType]
        }) : /*#__PURE__*/_jsx(H3, {
          color: isMarketingThemeEnabled ? 'text-core-default' : 'text-core-on-fill-default',
          children: CONTENT_TYPE_MESSAGE[contentType]
        }), description &&
        /*#__PURE__*/
        // @bend-expect trellis-tokenized
        _jsx(Description, {
          $use: isMarketingThemeEnabled ? 'on-light' : 'on-dark',
          dangerouslySetInnerHTML: {
            __html: description
          },
          "data-test-id": "public-course-description"
        })]
      }), /*#__PURE__*/_jsx(View, {
        position: "relative",
        children: /*#__PURE__*/_jsx(View, {
          backgroundColor: "fill-field-default-alt",
          boxShadow: "400",
          inlineSize: "100%",
          minBlockSize: "800px",
          position: isMobile ? 'relative' : 'absolute',
          ref: ref,
          children: /*#__PURE__*/_jsx(EmbeddedSignup, {
            id: id,
            pagePath: pagePath,
            type: contentType
          })
        })
      })]
    })]
  });
});
export default PublicLandingPagePrimaryDetails;